import {
  INITIATIVE_STATUS_LABELS,
  OBJECTIVE_STATUS_LABELS,
  formatDate,
  objectivePanelSchema,
  objectiveSchema,
  strategyMapSchema,
  targetPeriodLabel,
  TRAFFIC_LIGHT_LABELS,
  TRAFFIC_LIGHTS,
  type StrategyMap,
  type TrafficLight,
} from '@fluxus/shared';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ArrowRight, FolderKanban, Lock, Sigma, Trash2, Waypoints } from 'lucide-react';
import { useId, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { ProgressBar } from '@/components/progress/ProgressBar';
import { StatusBadge } from '@/components/status/StatusBadge';
import { useTheme } from '@/components/theme/theme';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { NativeSelect } from '@/components/ui/select';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { INITIATIVE_STATUS_LIGHT } from '@/features/initiatives/status';
import { CollaborationPanel } from '@/features/collaboration/CollaborationPanel';
import { MONTHS, Trend, formatValue, lightLabel, withUnit } from '@/features/objectives/performance';
import { drillHref, usePeriodParam } from '@/features/drill/drill';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { useMediaQuery } from '@/lib/use-media-query';

/** Objetivo sem KPI próprio: a performance vem dos objetivos que o desdobram (DEC-032). */
const CONSOLIDATED = 'consolidado dos desdobramentos';
const sourceNote = (source: string | null) => (source === 'CHILDREN' ? `, ${CONSOLIDATED}` : '');

const NODE_STYLE: Record<TrafficLight, string> = {
  green: 'border-status-green-solid bg-status-green-bg',
  yellow: 'border-status-yellow-solid bg-status-yellow-bg',
  red: 'border-status-red-solid bg-status-red-bg',
  gray: 'border-border-strong bg-surface-raised',
  blue: 'border-status-blue-solid bg-status-blue-bg',
};

/** Telas largas desenham o mapa; no celular, lista por perspectiva. */
const useWideScreen = () => useMediaQuery('(min-width: 768px)');

/** Estratégia › Mapa estratégico (T-044a, RF-17, PRD §11.8): causa e efeito com o semáforo do mês. */
export function MapaEstrategicoPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);

  return (
    <PageContainer>
      <PageHeader
        title="Mapa estratégico"
        description="Os objetivos de cada perspectiva com o semáforo do mês e as relações de causa e efeito. Clique num objetivo para ver a performance, os KPIs e os principais desvios."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="O mapa pertence a um projeto. Quando a empresa tiver um projeto em andamento, ele aparece aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-4">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          {project.status !== 'ACTIVE' ? (
            <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
              <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              Projeto arquivado: o mapa fica disponível só para consulta.
            </p>
          ) : null}
          <MapSection
            organizationId={organizationId}
            projectId={project.id}
            horizon={[project.horizonStart, project.horizonEnd]}
            readOnly={project.status !== 'ACTIVE'}
          />
        </div>
      )}
    </PageContainer>
  );
}

type MapRelation = { fromId: string; toId: string };

function MapSection({
  organizationId,
  projectId,
  horizon,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  horizon: [number, number];
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const can = useCan();
  const canEdit = !readOnly && can('objetivos', 'edit');
  const queryClient = useQueryClient();
  const wide = useWideScreen();
  const base = `/organizations/${organizationId}/projects/${projectId}`;
  const now = new Date();
  // O mês do link (drill-down — T-144c) ou o atual; o escolhido vai ao endereço.
  const [linked, setLinked] = usePeriodParam(horizon);
  const defaultYear = Math.min(Math.max(now.getFullYear(), horizon[0]), horizon[1]);
  const year = linked?.year ?? defaultYear;
  const month =
    linked?.month ?? (year === now.getFullYear() ? now.getMonth() + 1 : year < now.getFullYear() ? 12 : 1);
  const setYear = (y: number) => setLinked({ year: y, month });
  const setMonth = (m: number) => setLinked({ year, month: m });
  const [opened, setOpened] = useState<string | null>(null);
  const [removing, setRemoving] = useState<MapRelation | null>(null);
  const [busy, setBusy] = useState(false);
  const ids = { year: useId(), month: useId() };
  const years = Array.from({ length: horizon[1] - horizon[0] + 1 }, (_, i) => horizon[0] + i);

  const map = useQuery({
    queryKey: ['strategy-map', projectId, year, month],
    queryFn: ({ signal }) =>
      client.request(`${base}/strategy-map`, {
        query: { year: String(year), month: String(month) },
        schema: strategyMapSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const data = map.data;
  const names = useMemo(() => new Map(data?.objectives.map((o) => [o.id, o.name])), [data]);
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['strategy-map', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['objectives', projectId] }),
    ]);
  };

  /** Cria a relação de causa e efeito; devolve o motivo quando não deu certo (null = criada). */
  const createRelation = async ({ fromId, toId }: MapRelation): Promise<string | null> => {
    if (data?.relations.some((r) => r.fromId === fromId && r.toId === toId)) {
      return 'Esses objetivos já têm essa relação.';
    }
    try {
      await client.request(`${base}/objectives/${fromId}/relations`, {
        method: 'POST',
        body: { objectiveId: toId, type: 'CAUSE_EFFECT', via: 'strategy_map' },
        schema: objectiveSchema,
      });
      await refresh();
      toast.success('Relação criada', {
        description: `${names.get(fromId) ?? 'Objetivo'} contribui para ${names.get(toId) ?? 'objetivo'}.`,
      });
      return null;
    } catch (error) {
      return errorMessage(error);
    }
  };
  // Arraste no mapa: o motivo de uma recusa aparece no aviso.
  const connect = async (relation: MapRelation) => {
    if (relation.fromId === relation.toId) return;
    const problem = await createRelation(relation);
    if (problem) toast.error('Não foi possível criar a relação', { description: problem });
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await client.request(`${base}/objectives/${removing.fromId}/relations/${removing.toId}`, {
        method: 'DELETE',
        query: { type: 'CAUSE_EFFECT' },
        schema: objectiveSchema,
      });
      await refresh();
      toast.success('Relação removida');
      setRemoving(null);
    } catch (error) {
      toast.error('Não foi possível remover a relação', { description: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-28">
          <label htmlFor={ids.year} className="mb-1 block text-xs font-medium text-muted-foreground">
            Ano
          </label>
          <NativeSelect id={ids.year} value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="w-36">
          <label htmlFor={ids.month} className="mb-1 block text-xs font-medium text-muted-foreground">
            Mês
          </label>
          <NativeSelect id={ids.month} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                {targetPeriodLabel('MONTH', m)}
              </option>
            ))}
          </NativeSelect>
        </div>
        <ul aria-label="Legenda do semáforo" className="flex flex-wrap gap-1.5 pb-1.5">
          {TRAFFIC_LIGHTS.map((l) => (
            <li key={l}>
              <StatusBadge status={l} />
            </li>
          ))}
        </ul>
      </div>

      {map.isPending ? (
        <LoadingState rows={5} label="Carregando o mapa" />
      ) : map.isError ? (
        <ErrorState error={map.error} onRetry={() => void map.refetch()} retrying={map.isFetching} />
      ) : data!.objectives.length === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={Waypoints}
          title="Nenhum objetivo no mapa"
          description="Cadastre os objetivos em cada perspectiva; eles aparecem aqui com o semáforo do mês e as relações de causa e efeito."
          action={
            <Button asChild variant="outline">
              <Link to={`/empresas/${organizationId}/estrategia/objetivos`}>Ir para os objetivos</Link>
            </Button>
          }
        />
      ) : (
        <div aria-busy={map.isFetching} className="flex flex-col gap-4">
          <p className="text-xs text-muted-foreground">
            Semáforo de {targetPeriodLabel('MONTH', data!.month).toLowerCase()}/{data!.year}
            {data!.computedAt
              ? `, calculado em ${new Date(data!.computedAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}.`
              : ': ainda não há cálculo para este ano (cinza = sem dados).'}
            {canEdit && wide
              ? ' Para relacionar, arraste do ponto de baixo de um objetivo até outro; para remover, clique na seta.'
              : ''}
          </p>
          {wide ? (
            <MapCanvas
              data={data!}
              canEdit={canEdit}
              onOpen={setOpened}
              onConnect={connect}
              onEdge={setRemoving}
            />
          ) : (
            <MapList data={data!} onOpen={setOpened} />
          )}
          <RelationsSection
            data={data!}
            canEdit={canEdit}
            onAdd={createRelation}
            onRemove={setRemoving}
            names={names}
          />
        </div>
      )}

      <Sheet open={opened !== null} onOpenChange={(o) => !o && setOpened(null)}>
        <SheetContent
          title={opened ? (names.get(opened) ?? 'Objetivo') : 'Objetivo'}
          description={`Performance de ${targetPeriodLabel('MONTH', month).toLowerCase()}/${year}`}
          className="w-[min(30rem,100vw)]"
        >
          {opened ? (
            <ObjectivePanelView
              organizationId={organizationId}
              base={base}
              projectId={projectId}
              objectiveId={opened}
              year={year}
              month={month}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Remover a relação?"
        description={
          removing
            ? `${names.get(removing.fromId) ?? 'O objetivo'} deixa de contribuir para ${names.get(removing.toId) ?? 'o outro objetivo'} no mapa. A mudança fica registrada na auditoria.`
            : ''
        }
        confirmLabel="Remover relação"
        destructive
        busy={busy}
        onConfirm={() => void remove()}
      />
    </div>
  );
}

const LANE_LABEL = 190;
const NODE_W = 220;
const NODE_H = 96;
const GAP_X = 28;
const GAP_Y = 20;
const PAD = 22;
const PER_ROW = 4;
// Faixas alternadas deslocadas meia posição: a seta que atravessa uma faixa não passa por trás de um objetivo dela.
const STAGGER = (NODE_W + GAP_X) / 2;
const LANE_W = LANE_LABEL + PER_ROW * (NODE_W + GAP_X) + STAGGER + PAD;

type LaneData = { name: string; width: number; height: number };
type ObjectiveData = StrategyMap['objectives'][number] & { canEdit: boolean; onOpen: (id: string) => void };
type LaneNode = Node<LaneData, 'lane'>;
type ObjectiveNode = Node<ObjectiveData, 'objective'>;

function LaneView({ data }: NodeProps<LaneNode>) {
  return (
    <div
      className="flex rounded-lg border border-border bg-muted/40"
      style={{ width: data.width, height: data.height }}
    >
      <div className="flex w-[180px] shrink-0 items-center border-r border-border px-4">
        <p className="text-sm font-semibold text-balance text-muted-foreground">{data.name}</p>
      </div>
    </div>
  );
}

function ObjectiveView({ data }: NodeProps<ObjectiveNode>) {
  return (
    <div
      className={cn('flex h-[96px] w-[220px] flex-col rounded-lg border-2 shadow-xs', NODE_STYLE[data.light])}
      data-light={data.light}
    >
      {/* Sem alça o React Flow não desenha a seta: quem só consulta tem a alça, invisível e inerte. */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={data.canEdit}
        className={cn('!size-3 !border-2 !border-surface !bg-brand', !data.canEdit && '!opacity-0')}
      />
      <button
        type="button"
        className="nodrag nopan flex min-h-0 flex-1 max-md:min-h-11 flex-col items-start gap-1 rounded-md px-3 py-2 text-left focus-visible:outline-2 focus-visible:outline-ring"
        onClick={() => data.onOpen(data.id)}
      >
        {/* O nome acessível sai do próprio conteúdo (o texto visível faz parte dele — WCAG 2.5.3): "objetivo:
            performance (semáforo), responsável". O selo vem antes do responsável no DOM e à direita na tela. */}
        <span className="line-clamp-2 text-sm leading-snug font-semibold text-foreground">
          {data.name}
          <span className="sr-only">: </span>
        </span>
        <span className="mt-auto flex w-full flex-row-reverse items-center justify-between gap-2">
          <span className="flex shrink-0 items-center gap-1">
            {data.source === 'CHILDREN' ? (
              <Sigma className="size-3.5 text-muted-foreground" aria-hidden>
                <title>{CONSOLIDATED}</title>
              </Sigma>
            ) : null}
            <StatusBadge status={data.light} label={lightLabel(data.performance, data.light)} />
            <span className="sr-only">
              {` (${TRAFFIC_LIGHT_LABELS[data.light]}${sourceNote(data.source)}), `}
            </span>
          </span>
          <span className="truncate text-xs text-muted-foreground">
            {data.ownerName ?? 'Sem responsável'}
          </span>
        </span>
      </button>
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={data.canEdit}
        className={cn('!size-3 !border-2 !border-surface !bg-brand', !data.canEdit && '!opacity-0')}
      />
    </div>
  );
}

const nodeTypes = { lane: LaneView, objective: ObjectiveView };

/** Faixas e posições: cada perspectiva numa faixa, objetivos em linhas de até quatro (faixas alternadas deslocadas). */
function layoutMap(data: StrategyMap) {
  const lanes: LaneNode[] = [];
  const objectives: { id: string; x: number; y: number }[] = [];
  let y = 0;
  for (const [lane, p] of data.perspectives.entries()) {
    const items = data.objectives.filter((o) => o.perspectiveId === p.id);
    const rows = Math.max(1, Math.ceil(items.length / PER_ROW));
    const height = PAD * 2 + rows * NODE_H + (rows - 1) * GAP_Y;
    lanes.push({
      id: `lane:${p.id}`,
      type: 'lane',
      position: { x: 0, y },
      data: { name: p.name, width: LANE_W, height },
      draggable: false,
      selectable: false,
      connectable: false,
      focusable: false,
      zIndex: -1,
    });
    items.forEach((o, i) => {
      objectives.push({
        id: o.id,
        x: LANE_LABEL + PAD + (lane % 2) * STAGGER + (i % PER_ROW) * (NODE_W + GAP_X),
        y: y + PAD + Math.floor(i / PER_ROW) * (NODE_H + GAP_Y),
      });
    });
    y += height + 12;
  }
  return { lanes, objectives, height: y };
}

function MapCanvas({
  data,
  canEdit,
  onOpen,
  onConnect,
  onEdge,
}: {
  data: StrategyMap;
  canEdit: boolean;
  onOpen: (id: string) => void;
  onConnect: (r: MapRelation) => void;
  onEdge: (r: MapRelation) => void;
}) {
  const { resolved } = useTheme();
  const { nodes, edges, height } = useMemo(() => {
    const layout = layoutMap(data);
    const byId = new Map(data.objectives.map((o) => [o.id, o]));
    const objectiveNodes: ObjectiveNode[] = layout.objectives.map((p) => ({
      id: p.id,
      type: 'objective',
      position: { x: p.x, y: p.y },
      data: { ...byId.get(p.id)!, canEdit, onOpen },
      draggable: false,
      selectable: false,
      connectable: canEdit,
      width: NODE_W,
      height: NODE_H,
    }));
    const edgeList: Edge[] = data.relations.map((r) => ({
      id: `${r.fromId}->${r.toId}`,
      source: r.fromId,
      target: r.toId,
      markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18, color: 'var(--color-foreground)' },
      ariaLabel: `${byId.get(r.fromId)?.name ?? ''} contribui para ${byId.get(r.toId)?.name ?? ''}`,
      focusable: canEdit,
      selectable: false,
      interactionWidth: 18,
      style: { strokeWidth: 2 },
      // Acima dos cartões: a seta fica visível de ponta a ponta.
      zIndex: 1,
    }));
    return { nodes: [...layout.lanes, ...objectiveNodes] as Node[], edges: edgeList, height: layout.height };
  }, [data, canEdit, onOpen]);

  return (
    <div
      className="max-h-[78vh] min-h-80 overflow-hidden rounded-lg border border-border bg-surface"
      // Altura proporcional ao mapa: sem faixa vazia em volta quando ele é pequeno.
      style={{ aspectRatio: `${LANE_W + 40} / ${height + 40}` }}
      role="region"
      aria-label="Mapa estratégico"
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        colorMode={resolved}
        fitView
        fitViewOptions={{ padding: 0.06 }}
        minZoom={0.25}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={canEdit}
        elementsSelectable={false}
        edgesFocusable={canEdit}
        deleteKeyCode={null}
        proOptions={{ hideAttribution: true }}
        onConnect={(c: Connection) => void onConnect({ fromId: c.source, toId: c.target })}
        // Com o clique no nó, o React Flow mantém os eventos do cartão (nó não selecionável fica inerte).
        onNodeClick={(_, node) => node.type === 'objective' && onOpen(node.id)}
        onEdgeClick={canEdit ? (_, e) => onEdge({ fromId: e.source, toId: e.target }) : undefined}
        style={
          {
            '--xy-edge-stroke': 'var(--color-foreground)',
            '--xy-edge-stroke-selected': 'var(--color-brand)',
            '--xy-connectionline-stroke': 'var(--color-brand)',
            '--xy-background-color': 'transparent',
          } as CSSProperties
        }
      >
        <Background gap={24} size={1} color="var(--color-border)" />
        <Controls showInteractive={false} position="bottom-right" />
      </ReactFlow>
    </div>
  );
}

/** Celular: lista por perspectiva, na ordem das faixas, com o mesmo semáforo e as relações. */
function MapList({ data, onOpen }: { data: StrategyMap; onOpen: (id: string) => void }) {
  const names = new Map(data.objectives.map((o) => [o.id, o.name]));
  return (
    <div className="flex flex-col gap-4" aria-label="Mapa estratégico" role="region">
      {data.perspectives.map((p) => {
        const items = data.objectives.filter((o) => o.perspectiveId === p.id);
        return (
          <section key={p.id} aria-label={p.name}>
            <h2 className="mb-2 text-sm font-semibold text-muted-foreground">{p.name}</h2>
            {items.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border px-3 py-3 text-sm text-muted-foreground">
                Nenhum objetivo nesta perspectiva.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {items.map((o) => {
                  const contributes = data.relations.filter((r) => r.fromId === o.id);
                  return (
                    <li key={o.id}>
                      <button
                        type="button"
                        className={cn(
                          'flex w-full flex-col gap-1.5 rounded-lg border-2 px-3 py-2.5 text-left',
                          NODE_STYLE[o.light],
                        )}
                        onClick={() => onOpen(o.id)}
                      >
                        {/* Nome acessível do conteúdo: "objetivo: performance (semáforo), responsável…". */}
                        <span className="flex items-start justify-between gap-2">
                          <span className="text-sm font-semibold">
                            {o.name}
                            <span className="sr-only">: </span>
                          </span>
                          <span className="shrink-0">
                            <StatusBadge status={o.light} label={lightLabel(o.performance, o.light)} />
                            <span className="sr-only">{` (${TRAFFIC_LIGHT_LABELS[o.light]}), `}</span>
                          </span>
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {o.ownerName ?? 'Sem responsável'}
                          {o.source === 'CHILDREN' ? ` · ${CONSOLIDATED}` : ''}
                        </span>
                        {contributes.length ? (
                          <span className="flex items-start gap-1 text-xs">
                            <ArrowRight className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                            Contribui para {contributes.map((r) => names.get(r.toId)).join(', ')}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** Relações em texto: leitura por teclado e leitor de tela, e criar/remover sem arrastar. */
function RelationsSection({
  data,
  canEdit,
  onAdd,
  onRemove,
  names,
}: {
  data: StrategyMap;
  canEdit: boolean;
  /** Devolve o motivo da recusa (null = criada). */
  onAdd: (r: MapRelation) => Promise<string | null>;
  onRemove: (r: MapRelation) => void;
  names: Map<string, string>;
}) {
  const ids = { from: useId(), to: useId() };
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!fromId || !toId) return setError('Escolha os dois objetivos.');
    if (fromId === toId) return setError('Um objetivo não se relaciona com ele mesmo.');
    setError(null);
    setAdding(true);
    try {
      // Recusada (já existe, fora do escopo, erro do servidor): a escolha fica e o motivo aparece.
      const problem = await onAdd({ fromId, toId });
      if (problem) {
        setError(problem);
      } else {
        setFromId('');
        setToId('');
      }
    } finally {
      setAdding(false);
    }
  };
  const options = data.perspectives.map((p) => (
    <optgroup key={p.id} label={p.name}>
      {data.objectives
        .filter((o) => o.perspectiveId === p.id)
        .map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
    </optgroup>
  ));

  return (
    <section
      aria-labelledby={`${ids.from}-titulo`}
      className="rounded-lg border border-border bg-surface p-4"
    >
      <h2 id={`${ids.from}-titulo`} className="text-sm font-semibold">
        Relações de causa e efeito
      </h2>
      {data.relations.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">Nenhuma relação entre os objetivos do mapa.</p>
      ) : (
        <ul className="mt-2 flex flex-col divide-y divide-border">
          {data.relations.map((r) => (
            <li
              key={`${r.fromId}->${r.toId}`}
              className="flex items-center justify-between gap-2 py-1.5 text-sm"
            >
              <span className="min-w-0">
                {names.get(r.fromId)}{' '}
                <ArrowRight className="inline size-3.5 text-muted-foreground" aria-label="contribui para" />{' '}
                {names.get(r.toId)}
              </span>
              {canEdit ? (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remover relação ${names.get(r.fromId)} → ${names.get(r.toId)}`}
                  onClick={() => onRemove(r)}
                >
                  <Trash2 aria-hidden />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canEdit ? (
        <form
          onSubmit={(e) => void submit(e)}
          className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end"
          noValidate
        >
          <div className="flex-1">
            <label htmlFor={ids.from} className="mb-1 block text-xs font-medium text-muted-foreground">
              Objetivo que contribui (causa)
            </label>
            <NativeSelect id={ids.from} value={fromId} onChange={(e) => setFromId(e.target.value)}>
              <option value="">Escolha…</option>
              {options}
            </NativeSelect>
          </div>
          <div className="flex-1">
            <label htmlFor={ids.to} className="mb-1 block text-xs font-medium text-muted-foreground">
              Objetivo que recebe (efeito)
            </label>
            <NativeSelect id={ids.to} value={toId} onChange={(e) => setToId(e.target.value)}>
              <option value="">Escolha…</option>
              {options}
            </NativeSelect>
          </div>
          <Button type="submit" variant="outline" loading={adding}>
            Relacionar
          </Button>
        </form>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function ObjectivePanelView({
  organizationId,
  projectId,
  base,
  objectiveId,
  year,
  month,
}: {
  organizationId: string;
  projectId: string;
  base: string;
  objectiveId: string;
  year: number;
  month: number;
}) {
  const { client } = useAuth();
  const can = useCan();
  const panel = useQuery({
    queryKey: ['strategy-map', projectId, 'panel', objectiveId, year, month],
    queryFn: ({ signal }) =>
      client.request(`${base}/objectives/${objectiveId}/panel`, {
        query: { year: String(year), month: String(month) },
        schema: objectivePanelSchema,
        signal,
      }),
  });
  if (panel.isPending)
    return <LoadingState className="px-4" rows={4} label="Carregando o painel do objetivo" />;
  if (panel.isError) {
    return (
      <ErrorState
        className="px-4"
        error={panel.error}
        onRetry={() => void panel.refetch()}
        retrying={panel.isFetching}
      />
    );
  }
  const p = panel.data;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pt-3 pb-6">
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">Perspectiva</dt>
          <dd>{p.objective.perspective.name}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Situação</dt>
          <dd>{OBJECTIVE_STATUS_LABELS[p.objective.status]}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Responsável</dt>
          <dd>{p.objective.owner?.name ?? 'Sem responsável'}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Área</dt>
          <dd>{p.objective.department?.name ?? 'Empresa toda'}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Performance no mês</dt>
          <dd className="mt-0.5">
            {p.month ? (
              <StatusBadge
                status={p.month.light}
                label={lightLabel(p.month.performance, p.month.light)}
                size="md"
              />
            ) : (
              <StatusBadge status="gray" size="md" />
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Acumulado no ano</dt>
          <dd className="mt-0.5">
            {p.month ? (
              <StatusBadge
                status={p.month.ytdLight}
                label={lightLabel(p.month.ytdPerformance, p.month.ytdLight)}
                size="md"
              />
            ) : (
              <StatusBadge status="gray" size="md" />
            )}
          </dd>
        </div>
      </dl>

      {p.source === 'CHILDREN' ? (
        <p className="flex items-start gap-1.5 rounded-md bg-muted px-3 py-2 text-xs">
          <Sigma className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          Sem KPI próprio: a performance é a média ponderada dos objetivos que o desdobram.
        </p>
      ) : null}

      <Trend trend={p.trend} month={month} year={year} />

      {p.children.length ? (
        <section aria-labelledby={`${objectiveId}-desdobramentos`}>
          <h3 id={`${objectiveId}-desdobramentos`} className="text-sm font-semibold">
            Desdobrado em
          </h3>
          <ul className="mt-2 flex flex-col gap-1.5" aria-label="Objetivos que o desdobram">
            {p.children.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0">{c.name}</span>
                <StatusBadge status={c.light} label={lightLabel(c.performance, c.light)} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section aria-labelledby={`${objectiveId}-kpis`}>
        <h3 id={`${objectiveId}-kpis`} className="text-sm font-semibold">
          KPIs
        </h3>
        {p.kpis.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">Nenhum KPI ligado a este objetivo.</p>
        ) : (
          <ul className="mt-2 flex flex-col gap-2" aria-label="KPIs do objetivo">
            {p.kpis.map((k) => (
              <li key={k.id} className="rounded-md border border-border px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 text-sm">
                    <span className="font-mono text-xs text-muted-foreground">{k.code}</span>{' '}
                    <span className="font-medium">{k.name}</span>
                  </p>
                  <StatusBadge status={k.light} label={lightLabel(k.performance, k.light)} />
                </div>
                <p className="mt-1 text-xs text-muted-foreground tabular-nums">
                  Realizado {k.actual !== null ? withUnit(k.actual, k.unitLabel) : '—'} · Meta{' '}
                  {k.target !== null ? withUnit(k.target, k.unitLabel) : '—'}
                  {k.weight !== null ? ` · Peso ${formatValue(k.weight)}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby={`${objectiveId}-desvios`}>
        <h3 id={`${objectiveId}-desvios`} className="text-sm font-semibold">
          Principais desvios
        </h3>
        {p.deviations.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">Nenhum KPI abaixo da meta no mês.</p>
        ) : (
          <ol className="mt-2 flex flex-col gap-1.5" aria-label="Principais desvios">
            {p.deviations.map((d) => (
              <li key={d.kpiId} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0">
                  <span className="font-medium">{d.name}</span>
                  <span className="block text-xs text-muted-foreground tabular-nums">
                    {d.gap !== null
                      ? `${d.gap < 0 ? '−' : '+'}${withUnit(Math.abs(d.gap), d.unitLabel)} da meta`
                      : 'Sem meta ou realizado'}
                  </span>
                </span>
                <StatusBadge status={d.light} label={lightLabel(d.performance, d.light)} />
              </li>
            ))}
          </ol>
        )}
      </section>

      {p.initiatives ? (
        <section aria-labelledby={`${objectiveId}-iniciativas`}>
          <h3 id={`${objectiveId}-iniciativas`} className="text-sm font-semibold">
            Iniciativas
          </h3>
          {p.initiatives.length === 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Nenhuma iniciativa contribui para este objetivo ainda.
            </p>
          ) : (
            <ul className="mt-2 flex flex-col gap-2" aria-label="Iniciativas do objetivo">
              {p.initiatives.map((i) => (
                <li key={i.id} className="rounded-md border border-border px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/empresas/${organizationId}/execucao/iniciativas?iniciativa=${i.id}`}
                      className="min-w-0 text-sm font-medium underline-offset-2 hover:underline"
                    >
                      {i.name}
                    </Link>
                    <StatusBadge
                      status={INITIATIVE_STATUS_LIGHT[i.status]}
                      label={INITIATIVE_STATUS_LABELS[i.status]}
                    />
                    {i.late ? <StatusBadge status="red" label="Atrasada" /> : null}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {i.leader?.name ?? 'Sem líder'} ·{' '}
                    {i.endDate ? `Prazo ${formatDate(i.endDate)}` : 'Sem prazo'}
                  </p>
                  <ProgressBar value={i.progress} label={`Progresso de ${i.name}`} className="mt-1.5" />
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {can('objetivos', 'view') ? (
          <Button asChild variant="outline" size="sm">
            {/* No mês do mapa (drill-down — T-144c). */}
            <Link
              to={drillHref.objective(organizationId, {
                projectId,
                objectiveId: p.objective.id,
                year,
                month,
              })}
            >
              Abrir o objetivo
            </Link>
          </Button>
        ) : null}
        {p.kpis.length && can('metas-resultados', 'view') ? (
          <Button asChild variant="outline" size="sm">
            <Link
              to={`/empresas/${organizationId}/indicadores/resultados?projeto=${projectId}&ano=${year}&mes=${month}`}
            >
              Ver resultados
            </Link>
          </Button>
        ) : null}
      </div>
      <CollaborationPanel
        organizationId={organizationId}
        projectId={projectId}
        subject="objective"
        subjectId={objectiveId}
        label={p.objective.name}
      />
    </div>
  );
}
